import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Observable, BehaviorSubject, throwError, firstValueFrom } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Role } from '../admissions/interface';
import { jwtDecode } from 'jwt-decode';
import { Router } from '@angular/router';
import { PinnedFiltersService } from './pinned-filter.service'; // Add this import
import { CampusCacheService } from './cache/campus-cache';
import { TagsCacheService } from './cache/tags-cache';
import { UsersCacheService } from './cache/users-cache';
import { activityTypeCache } from './cache/activity-types-cache';
import { CallUIIntegrationService } from '../shared/call-ui-integration.service';
import { TelnyxWebRTCService } from './telnyx-webrtc.service';
import { ThemeService } from './theme.service';
import { QuickFilterStateService } from './quick-filter-state.service';
import { FirebaseService } from './firebase.service';
import { userDataService } from './userDataService';
import { CallFeatureService } from './call-feature.service';
import { companyService } from './company.service';

@Injectable({
  providedIn: 'root',
})
export class authService {
  private httpService;
  private userRole: Role;
  
  // Add auth state management
  private authStateSubject = new BehaviorSubject<boolean>(this.isAuthenticated());
  public authState$ = this.authStateSubject.asObservable();
  
  private currentUserSubject = new BehaviorSubject<any>(this.getCurrentUser());
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor(
    http: HttpClient,
    private router: Router,
    private pinnedFiltersService: PinnedFiltersService, // Add this injection
    private usersCache: UsersCacheService,
    private campusCache: CampusCacheService,
    private tagsCache: TagsCacheService,
    private activityTypeCache: activityTypeCache,
    private injector: Injector, // Add this
    private themeService: ThemeService, // Add this
    private quickFilterStateService: QuickFilterStateService,
    public notificationService:FirebaseService,
    private userDataService:userDataService,
    private callFeatureService: CallFeatureService,
    private companyService: companyService
  ) {
    this.httpService = http;
  }

  login(body: any, params?: any): Observable<any> {
    return this.httpService.post(
      `${environment.apiUrl}/auth/login`,
      body,
      params,
    ).pipe(
      tap(async (response: any) => {
        if (response.access_token) {

          this.userDataService.setUserData(response.user);

          await this.setBearer(response.access_token);
          await this.setUserRole(response.role);
          localStorage.setItem('sessionId', response.session_id);
          
          // Clear any existing pinned filters before setting new user
          this.pinnedFiltersService.clearAllData();
          
          this.authStateSubject.next(true);
          const decodedToken = this.getDecodedToken(response.access_token);
          this.currentUserSubject.next(decodedToken);
          localStorage.removeItem('pipeline-view-mode');
          
          // Clear engagement hub tab position and user filter on login
          localStorage.removeItem('engagement-hub-last-tab');
          localStorage.removeItem('engagement-hub-user-filter');
          localStorage.removeItem('engagement-hub-steps-filter');
          
          // CLEAR QUICK FILTERS ON LOGIN
          localStorage.removeItem('leads-quick-filters');
          localStorage.removeItem('repStatsGlobalFilter')
          localStorage.removeItem('pipeline-quick-filters');
          // Or use the service method:
          // this.quickFilterStateService.clearQuickFilters('all');
          
          // Clear all filter-cleared states from session storage on login
          this.clearAllFilterStates();
          
          // Clear only default owner filters on login
          this.clearDefaultOwnerFilters();
          
          // Clear engagement hub tab position
          this.clearEngagementHubTabPosition();
          
          // Apply user's theme preference from login response
          if (response.user?.preferred_theme) {
            this.themeService.setThemeFromUser(response.user.preferred_theme);
          }

          // Load company data and initialize call features
          if(response.role !=='LEAD'){
            try {
              // Load company data to get enable_calls flag
              await this.loadCompanyData();

              // Initialize call UI integration if calls are enabled
              if (this.callFeatureService.isCallsEnabled()) {
                const callUIIntegration = this.injector.get(CallUIIntegrationService);
                await callUIIntegration.initialize();
              } else {
              }
            } catch (error) {
              console.error('⚠️ Failed to initialize call features:', error);
              // Non-critical - continue without call service
            }
          }

        }
      }),
      catchError(error => {
        this.authStateSubject.next(false);
        return throwError(() => error);
      })
    );
  }

  /**
   * Load company data including call feature flag
   */
  private async loadCompanyData(): Promise<void> {
    try {
      const response: any = await this.companyService.get().toPromise();
      if (response && response.company) {
        // Store company data in localStorage
        localStorage.setItem('companyData', JSON.stringify(response.company));

        // Update call feature service
        this.callFeatureService.updateCallFeatureStatus(response.company.enable_calls === true);
      }
    } catch (error) {
      console.error('❌ Failed to load company data:', error);
      // Set calls disabled by default on error
      this.callFeatureService.updateCallFeatureStatus(false);
    }
  }

  private clearAllCaches(): void {
    this.usersCache.clearCache();
    this.campusCache.clearCache();
    this.tagsCache.clearCache();
    this.activityTypeCache.clearCache();
  }
  
  // Add this helper method to clear all filter states
  private clearAllFilterStates(): void {
    // Get all keys from sessionStorage
    const keysToRemove: string[] = [];
    
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.includes('-filter-cleared')) {
        keysToRemove.push(key);
      }
    }
    
    // Remove all filter-cleared keys
    keysToRemove.forEach(key => {
      sessionStorage.removeItem(key);
    });
  }

  // New method to clear only default owner filters
// New method to clear only default owner filters
private clearDefaultOwnerFilters(): void {
  const user = JSON.parse(localStorage.getItem("userData") || "{}");
  if (!user?.id) return;

  const filterKeys = [
    `user_${user.id}_lead-filter`,
    `user_${user.id}_lead-pipeline-filter`,
    `user_${user.id}_activity-filter`
  ];

  filterKeys.forEach(key => {
    const filterData = localStorage.getItem(key);
    if (filterData) {
      try {
        const filter = JSON.parse(filterData);
        // Only remove if it's a default owner filter
        if (filter.isDefaultFilter || filter.isUserFilter) {
          localStorage.removeItem(key);
        }
      } catch (error) {
        console.error(`Error parsing filter ${key}:`, error);
      }
    }
  });

  // Clear tab indices to reset to default view
  const tabKeys = [
    'leads-last-tab-index',
    'leads-pipeline-last-tab-index',
    'activities-last-tab-index'
  ];

  tabKeys.forEach(key => localStorage.removeItem(key));
}

  /**
   * Clear engagement hub tab position on login to reset to "Fresh Leads" tab
   */
  private clearEngagementHubTabPosition(): void {
    try {
      localStorage.removeItem('engagement-hub-last-tab');
    } catch (error) {
      console.error('❌ Error clearing engagement hub tab position:', error);
    }
  }

  changePassword(body: any, params?: any): Observable<any> {
    return this.httpService.patch(
      `${environment.apiUrl}/auth/changePassword`,
      body,
      params,
    );
  }

  forgetPasswordRequest(data){
    return this.httpService.post(
      `${environment.apiUrl}/auth/request-password-reset`,
      data,
    );
  }
  
  resetPassword(data){
    return this.httpService.post(
      `${environment.apiUrl}/auth/reset-password`,
      data,
    );
  }

  validateToken(data){
    return this.httpService.post(
      `${environment.apiUrl}/auth/validate-token`,
      data,
    );
  }
  
  getSignUrl(key: string, expSeconds: string) {
    const params = { key, expSeconds };
    return this.httpService.get(`${environment.apiUrl}/aws/signUrlNoPublic`, { params });
  }

  getSignUrls(data:any){
    return this.httpService.post(
      `${environment.apiUrl}/aws/signUrls`,
      data,
    );
  }


  async logout(reason: string = 'manual') {
    // Terminate any active call before logout
    try {
      const telnyxService = this.injector.get(TelnyxWebRTCService);
      if (telnyxService.currentCall()) {
        telnyxService.hangupCall();
      }
    } catch (error) {
      console.warn('Could not terminate call during logout:', error);
    }
  
    // ── Get userId BEFORE clearing localStorage ───────────────────────────
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const userId = userData?.id;
  
    const sessionId = localStorage.getItem('sessionId');
    try {
      if (sessionId) {
        await firstValueFrom(
          this.httpService.post(`${environment.apiUrl}/auth/close-session`, {
            session_id: parseInt(sessionId),
            reason: reason,
          })
        ).catch(() => {});
      }
    } catch {}
  
    // ── Clear sticky notes state for this user ────────────────────────────
    if (userId) {
      const { StickyNotesService } = await import('../services/sticky-notes.service');
      this.injector.get(StickyNotesService).clear(userId);
    }
  
    // rest of logout unchanged...
    this.pinnedFiltersService.clearAllData();
    this.clearDefaultOwnerFilters();
    this.clearAllFilterStates();
  
    localStorage.removeItem('engagement-hub-last-tab');
    localStorage.removeItem('engagement-hub-user-filter');
    localStorage.removeItem('engagement-hub-steps-filter');
    localStorage.removeItem('sessionId');
  
    localStorage.removeItem('accessToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('role');
    localStorage.removeItem('fcmToken');
    localStorage.removeItem('companyTabId');
    localStorage.removeItem('companyData');
  
    localStorage.removeItem('show-pinned-filters');
    localStorage.removeItem('show-pinned-filters-pipeline');
  
    this.clearAllCaches();
    this.callFeatureService.updateCallFeatureStatus(false);
    this.authStateSubject.next(false);
    this.currentUserSubject.next(null);
  
    this.router.navigate(['/authentication/login']);
  }

  setBearer(bearer: string) {
    localStorage.setItem('accessToken', bearer);
  }

  setEmail(email: string) {
    localStorage.setItem('rememberEmail', email);
  }

  getEmail() {
    return localStorage.getItem('rememberEmail');
  }

  removeEmail() {
    localStorage.removeItem('rememberEmail');
  }


  getBearer() {
    return localStorage.getItem('accessToken');
  }

  setUserRole(role: Role) {
    this.userRole = role;
    localStorage.setItem('role', JSON.stringify(role));
  }

  getUserRole(): Role {
    if (!this.userRole) {
      this.userRole = JSON.parse(localStorage.getItem('role'));
    }
    return this.userRole;
  }

  getToken(): string | null {
    return localStorage.getItem('accessToken');
  }

  // Check if the user is authenticated
  isAuthenticated(): boolean {
    const token = this.getToken();
    return token !== null && !this.isTokenExpired(token);
  }

  getAccessToken(): string {
    const accessToken = localStorage.getItem('accessToken');
    return accessToken ? accessToken : null;
  }


  getDecodedToken(token: string): any {
    const decodedToken = jwtDecode(token);
    return decodedToken;
  }

  isTokenExpired(token: string): boolean {
    if (!token) return true;
    
    try {
      const expiry = this.getDecodedToken(token).exp;
      return Math.floor(new Date().getTime() / 1000) >= expiry;
    } catch {
      return true;
    }
  }

  onGetNewAccessToken() {
    return this.httpService.post(
      `${environment.apiUrl}/auth/token`,
      {},
      // Remove the responseType: 'text' option to allow JSON parsing
    ).pipe(
      tap((response: any) => {
        // Handle both string and object responses
        const newToken = typeof response === 'string' ? response : response.access_token;
        
        if (newToken) {
          this.setBearer(newToken);
          const decodedToken = this.getDecodedToken(newToken);
          this.currentUserSubject.next(decodedToken);
        }
      })
    );
  }

  getCurrentUser(): any {
    const token = this.getAccessToken();
    if (!token || this.isTokenExpired(token)) {
      return null;
    }
    try {
      return this.getDecodedToken(token);
    } catch {
      return null;
    }
  }

  generateApiToken(): Observable<any> {
    return this.httpService.post(`${environment.apiUrl}/auth/api-token`, {});
  }
  
  getApiToken(): Observable<any> {
    return this.httpService.get(`${environment.apiUrl}/auth/api-token`);
  }
  
  revokeApiToken(): Observable<any> {
    return this.httpService.delete(`${environment.apiUrl}/auth/api-token`);
  }
}